import React, { useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRooms } from '../../context/RoomContext';
import { Home, Hotel, Calendar, Settings, Users, DollarSign, Menu, X, FileSpreadsheet } from 'lucide-react';

const AdminDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { rooms, bookings } = useRooms();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  if (!currentUser?.isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
        <Link
          to="/"
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Back to Home
        </Link>
      </div>
    );
  }
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isDashboardHome = location.pathname === '/admin';

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: Home, current: isActive('/admin') },
    { name: 'Rooms', href: '/admin/rooms', icon: Hotel, current: isActive('/admin/rooms') },
    { name: 'Bookings', href: '/admin/bookings', icon: Calendar, current: isActive('/admin/bookings') },
    { name: 'Accounts', href: '/admin/accounts', icon: DollarSign, current: isActive('/admin/accounts') },
    { name: 'Reports', href: '/admin/reports', icon: FileSpreadsheet, current: isActive('/admin/reports') },
    { name: 'Settings', href: '/admin/settings', icon: Settings, current: isActive('/admin/settings') },
  ];

  const closeSidebar = () => setSidebarOpen(false);
  
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex h-screen">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={closeSidebar}></div>
          </div>
        )}

        {/* Sidebar */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-indigo-800 text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="flex items-center justify-between h-16 px-4 bg-indigo-900">
            <div className="flex items-center space-x-2">
              <Hotel className="h-8 w-8" />
              <h1 className="text-xl font-bold">Admin Panel</h1>
            </div>
            <button
              onClick={closeSidebar}
              className="lg:hidden text-indigo-200 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          
          <nav className="mt-8 px-4">
            <div className="space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={closeSidebar}
                    className={`
                      flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors duration-200
                      ${item.current
                        ? 'bg-indigo-900 text-white'
                        : 'text-indigo-100 hover:bg-indigo-700 hover:text-white'
                      }
                    `}
                  >
                    <Icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
        
        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top header */}
          <header className="bg-white shadow-sm border-b border-gray-200">
            <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                >
                  <Menu className="h-6 w-6" />
                </button>
                <h1 className="ml-4 lg:ml-0 text-xl sm:text-2xl font-bold text-gray-900">
                  {isActive('/admin') && 'Dashboard'}
                  {isActive('/admin/rooms') && 'Manage Rooms'}
                  {isActive('/admin/bookings') && 'Manage Bookings'}
                  {isActive('/admin/accounts') && 'Accounts'}
                  {isActive('/admin/reports') && 'Reports'}
                  {isActive('/admin/settings') && 'Settings'}
                </h1>
              </div>
              
              {/* User info */}
              <div className="flex items-center space-x-4">
                <div className="hidden sm:block text-sm text-gray-700">
                  Welcome, {currentUser.name}
                </div>
                <div className="h-8 w-8 bg-indigo-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {currentUser.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </header>

          {/* Main content area */}
          <main className="flex-1 overflow-y-auto bg-gray-50">
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
              {isDashboardHome ? (
                <div className="space-y-6">
                  {/* Stats cards */}
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {/* Total Rooms Card */}
                    <div className="bg-white overflow-hidden shadow-sm rounded-lg hover:shadow-md transition-shadow duration-200">
                      <div className="p-5">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                            <Hotel className="h-6 w-6 text-white" />
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-sm font-medium text-gray-500 truncate">
                                Total Rooms
                              </dt>
                              <dd>
                                <div className="text-2xl font-bold text-gray-900">
                                  {rooms.length}
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                          <Link
                            to="/admin/rooms"
                            className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
                          >
                            Manage rooms →
                          </Link>
                        </div>
                      </div>
                    </div>
                    
                    {/* Total Bookings Card */}
                    <div className="bg-white overflow-hidden shadow-sm rounded-lg hover:shadow-md transition-shadow duration-200">
                      <div className="p-5">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                            <Calendar className="h-6 w-6 text-white" />
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-sm font-medium text-gray-500 truncate">
                                Total Bookings
                              </dt>
                              <dd>
                                <div className="text-2xl font-bold text-gray-900">
                                  {bookings.length}
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                          <Link
                            to="/admin/bookings"
                            className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
                          >
                            View all bookings →
                          </Link>
                        </div>
                      </div>
                    </div>
                    
                    {/* Active Bookings Card */}
                    <div className="bg-white overflow-hidden shadow-sm rounded-lg hover:shadow-md transition-shadow duration-200">
                      <div className="p-5">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                            <Users className="h-6 w-6 text-white" />
                          </div>
                          <div className="ml-5 w-0 flex-1">
                            <dl>
                              <dt className="text-sm font-medium text-gray-500 truncate">
                                Active Bookings
                              </dt>
                              <dd>
                                <div className="text-2xl font-bold text-gray-900">
                                  {bookings.filter(b => b.status === 'confirmed').length}
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 px-5 py-3">
                        <div className="text-sm">
                          <Link
                            to="/admin/accounts"
                            className="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
                          >
                            View accounts →
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Recent Bookings */}
                  <div className="bg-white shadow-sm rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium text-gray-900">Recent Bookings</h2>
                        <Link
                          to="/admin/bookings"
                          className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          View all
                        </Link>
                      </div>
                      
                      {bookings.length > 0 ? (
                        <div className="overflow-hidden">
                          <ul className="divide-y divide-gray-200">
                            {bookings.slice(0, 5).map((booking) => {
                              const room = rooms.find(r => r.id === booking.roomId);
                              return (
                                <li key={booking.id} className="py-4">
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-indigo-600 truncate">
                                        IFA-{booking.id.substring(0, 8)} - {booking.userName}
                                      </p>
                                      <div className="mt-1 flex items-center text-sm text-gray-500">
                                        <Hotel className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                        <span className="truncate">{room?.name || 'Unknown Room'}</span>
                                      </div>
                                      <div className="mt-1 flex items-center text-sm text-gray-500">
                                        <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                        <span>
                                          {booking.checkIn} to {booking.checkOut}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                                      {booking.source && booking.source !== 'Manual' && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                          {booking.source}
                                        </span>
                                      )}
                                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        booking.status === 'confirmed'
                                          ? 'bg-green-100 text-green-800'
                                          : booking.status === 'pending'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : 'bg-red-100 text-red-800'
                                      }`}>
                                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                                      </span>
                                    </div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ) : (
                        <div className="text-center py-6">
                          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings yet</h3>
                          <p className="mt-1 text-sm text-gray-500">Get started by creating your first booking.</p>
                          <div className="mt-6">
                            <Link
                              to="/admin/bookings"
                              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                            >
                              <Calendar className="mr-2 h-4 w-4" />
                              Create booking
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <Outlet />
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;